import {
  KeepClientConfig,
  ClaimSubject,
  ClaimResource,
  ClaimDecision,
  BatchClaimResult,
  EffectivePermissions,
  EffectivePermissionsContext,
  ServiceTokenResponse,
  ServiceTokenVerification,
  ApiKeyVerification,
  Account,
  Session,
  Organization,
  Team,
  OrgMember,
  TeamMember,
  Invitation,
  FindOrCreateServiceIdentityRequest,
  ServiceIdentityProvisionResponse,
  BindRoleRequest,
  RoleBindingResponse,
  DeviceCodePreauthRequest,
  DeviceCodePreauthResponse,
  ManagedSifterReveal,
  ManagedSifterStatus,
  GithubTokenReveal,
} from './types';

export class KeepClient {
  private baseUrl: string;
  private headers: Record<string, string>;

  constructor(config: KeepClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.headers = { 'Content-Type': 'application/json' };

    if (config.serviceSecret) {
      this.headers['X-Service-Secret'] = config.serviceSecret;
    }
    if (config.serviceToken) {
      this.headers['Authorization'] = `Bearer ${config.serviceToken}`;
    } else if (config.accessToken) {
      this.headers['Authorization'] = `Bearer ${config.accessToken}`;
    }
  }

  // ── Claims ──────────────────────────────────────────────────────

  async checkClaim(
    subject: ClaimSubject,
    scope: string,
    resource: ClaimResource
  ): Promise<ClaimDecision> {
    return this.request<ClaimDecision>('POST', '/api/claims', {
      subject,
      scope,
      resource,
    });
  }

  async checkClaimsBatch(
    subject: ClaimSubject,
    claims: Array<{ scope: string; resource: ClaimResource }>
  ): Promise<{ results: BatchClaimResult[] }> {
    return this.request<{ results: BatchClaimResult[] }>('POST', '/api/claims/batch', {
      subject,
      claims,
    });
  }

  async getEffectivePermissions(
    subject: ClaimSubject,
    context: EffectivePermissionsContext
  ): Promise<EffectivePermissions> {
    return this.request<EffectivePermissions>('POST', '/api/claims/effective', {
      subject,
      context,
    });
  }

  // ── API Key Verification ────────────────────────────────────────

  async verifyApiKey(key: string): Promise<ApiKeyVerification> {
    return this.request<ApiKeyVerification>('POST', '/api/keys/claims', { key });
  }

  // ── Service Tokens ──────────────────────────────────────────────

  async exchangeServiceToken(
    clientId: string,
    clientSecret: string
  ): Promise<ServiceTokenResponse> {
    return this.request<ServiceTokenResponse>('POST', '/api/service-tokens', {
      clientId,
      clientSecret,
    });
  }

  async verifyServiceToken(token: string): Promise<ServiceTokenVerification> {
    return this.request<ServiceTokenVerification>('POST', '/api/service-tokens/claims', {
      token,
    });
  }

  // ── Identity ────────────────────────────────────────────────────

  async createAccount(email: string, password: string, name?: string): Promise<Account> {
    return this.request<Account>('POST', '/api/accounts', { email, password, name });
  }

  async createSession(email: string, password: string): Promise<Session> {
    return this.request<Session>('POST', '/api/sessions', { email, password });
  }

  async destroySession(refreshToken?: string): Promise<void> {
    await this.request<void>('DELETE', '/api/sessions', refreshToken ? { refreshToken } : undefined);
  }

  async refreshSession(refreshToken: string): Promise<Session> {
    return this.request<Session>('POST', '/api/sessions/renewal', { refreshToken });
  }

  async getCurrentUser(): Promise<Account> {
    return this.request<Account>('GET', '/api/accounts/me');
  }

  async updateProfile(updates: { name?: string; avatarUrl?: string }): Promise<Account> {
    return this.request<Account>('PATCH', '/api/accounts/me', updates);
  }

  async confirmEmail(email: string, code: string): Promise<void> {
    await this.request<void>('POST', '/api/email-confirmations', { email, code });
  }

  async requestVerificationCode(email: string): Promise<void> {
    await this.request<void>('POST', '/api/verification-codes', { email });
  }

  async requestPasswordReset(email: string): Promise<void> {
    await this.request<void>('POST', '/api/password-resets', { email });
  }

  async resetPassword(email: string, code: string, newPassword: string): Promise<void> {
    await this.request<void>('PUT', `/api/password-resets/${code}`, { email, newPassword });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.request<void>('PUT', '/api/passwords', { currentPassword, newPassword });
  }

  // ── Organizations ───────────────────────────────────────────────

  async listOrganizations(): Promise<Organization[]> {
    const { organizations } = await this.request<{ organizations: Organization[]; total: number }>(
      'GET',
      '/api/organizations',
    );
    return organizations;
  }

  async getOrganization(orgId: string): Promise<Organization> {
    return this.request<Organization>('GET', `/api/organizations/${orgId}`);
  }

  async createOrganization(name: string, description?: string): Promise<Organization> {
    return this.request<Organization>('POST', '/api/organizations', { name, description });
  }

  async updateOrganization(orgId: string, updates: { name?: string; description?: string }): Promise<Organization> {
    return this.request<Organization>('PATCH', `/api/organizations/${orgId}`, updates);
  }

  async deleteOrganization(orgId: string): Promise<void> {
    await this.request<void>('DELETE', `/api/organizations/${orgId}`);
  }

  async listMembers(orgId: string): Promise<OrgMember[]> {
    return this.request<OrgMember[]>('GET', `/api/organizations/${orgId}/members`);
  }

  async removeMember(orgId: string, userId: string): Promise<void> {
    await this.request<void>('DELETE', `/api/organizations/${orgId}/members/${userId}`);
  }

  async listTeams(orgId: string): Promise<Team[]> {
    return this.request<Team[]>('GET', `/api/organizations/${orgId}/teams`);
  }

  async createTeam(orgId: string, name: string, description?: string): Promise<Team> {
    return this.request<Team>('POST', `/api/organizations/${orgId}/teams`, { name, description });
  }

  async listTeamMembers(orgId: string, teamId: string): Promise<TeamMember[]> {
    return this.request<TeamMember[]>('GET', `/api/organizations/${orgId}/teams/${teamId}/members`);
  }

  async addTeamMember(orgId: string, teamId: string, userId: string): Promise<TeamMember> {
    return this.request<TeamMember>('POST', `/api/organizations/${orgId}/teams/${teamId}/members`, { userId });
  }

  async createInvitation(orgId: string, email: string, roleGrants?: string[]): Promise<Invitation> {
    return this.request<Invitation>('POST', `/api/organizations/${orgId}/invitations`, { email, roleGrants });
  }

  async getInvitation(token: string): Promise<Invitation> {
    return this.request<Invitation>('GET', `/api/invitations/${token}`);
  }

  async acceptInvitation(token: string): Promise<void> {
    await this.request<void>('POST', `/api/invitations/${token}/acceptance`);
  }

  // ── Substrate Provisioning ──────────────────────────────────────

  /**
   * Find-or-create a Keep agent (service identity) on behalf of the
   * given principal. Idempotent on `(ownerPrincipalId, name)`; returns
   * `clientSecret: null` on re-find.
   *
   * Requires `keep:service-identities:write`.
   */
  async findOrCreateServiceIdentity(
    request: FindOrCreateServiceIdentityRequest,
  ): Promise<ServiceIdentityProvisionResponse> {
    return this.request<ServiceIdentityProvisionResponse>(
      'POST',
      `/api/users/${request.ownerPrincipalId}/service-identities`,
      request,
    );
  }

  /**
   * Bind a role (by name) to a service identity. Pool-scoped bindings
   * pass `scopeRefs: [{kind: 'jobPool', poolId: ...}]`. User-owned
   * pools pass `userId` as the structural principal; team/org-owned
   * pools pass `teamId`/`orgId`.
   *
   * Requires `keep:role-bindings:write`.
   */
  async bindServiceIdentityRole(
    serviceIdentityId: string,
    request: BindRoleRequest,
  ): Promise<RoleBindingResponse> {
    return this.request<RoleBindingResponse>(
      'POST',
      `/api/service-identities/${serviceIdentityId}/role-bindings`,
      request,
    );
  }

  /**
   * Mint a pre-authorised device-code (pairing code) bound to an existing
   * service identity. The Sift "Pair your Sifter" button calls this and
   * shows `userCode` to the user.
   *
   * Requires `keep:service-identities:write`.
   */
  async createDeviceCodePreauth(
    request: DeviceCodePreauthRequest,
  ): Promise<DeviceCodePreauthResponse> {
    return this.request<DeviceCodePreauthResponse>('POST', '/api/oauth/device-code/preauth', {
      client_id: request.clientId,
      boundAgentId: request.boundAgentId,
      ttlSeconds: request.ttlSeconds,
    });
  }

  // ── Managed Sifter ──────────────────────────────────────────────

  /**
   * Reveal a hosted-Sifter customer's plaintext Anthropic API key for
   * one dispatch. Service-token only; requires the
   * `keep:managed-sifter-keys:reveal` scope, granted only to the
   * `whittle-hosted-sifter` service identity.
   *
   * Callers must use the returned key for a single Anthropic request
   * and drop it from memory afterwards. Keep stamps `last_used_at` on
   * every call and is the audit-of-record for who decrypted whose key
   * when.
   */
  async revealManagedSifterKey(userId: string): Promise<ManagedSifterReveal> {
    return this.request<ManagedSifterReveal>(
      'POST',
      `/api/managed-sifters/${encodeURIComponent(userId)}/reveal-key`,
    );
  }

  /**
   * Read a user's hosted-Sifter opt-in status. Returns `{enabled:
   * false}` for users who never opted in (no row) and users who
   * opted in then disabled. Service-to-service only; requires the
   * `keep:managed-sifter-keys:read-status` scope, which is distinct
   * from `:reveal` so a service identity can hold one without the
   * other.
   */
  async getManagedSifterStatus(userId: string): Promise<ManagedSifterStatus> {
    return this.request<ManagedSifterStatus>(
      'GET',
      `/api/users/${encodeURIComponent(userId)}/managed-sifter-status`,
    );
  }

  // ── GitHub OAuth token ──────────────────────────────────────────

  /**
   * Reveal a user's decrypted GitHub OAuth token for a service-to-service
   * call on their behalf (e.g. the hosted Sifter cloning the repo under
   * review). Service-token only; requires `keep:oauth-tokens:read`. Treat
   * the returned token as a single-use, in-memory secret. Throws (404 →
   * NotFound) when the user has not connected GitHub.
   */
  async getUserGithubToken(userId: string): Promise<GithubTokenReveal> {
    return this.request<GithubTokenReveal>(
      'GET',
      `/api/users/${encodeURIComponent(userId)}/github-token`,
    );
  }

  // ── HTTP ────────────────────────────────────────────────────────

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.baseUrl}${path}`;

    const response = await fetch(url, {
      method,
      headers: this.headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const json = await response.json() as {
      success: boolean;
      data: T;
      error?: { code: string; message: string };
    };

    if (!response.ok || !json.success) {
      const errorMessage = json.error?.message ?? `Request failed with status ${response.status}`;
      const error = new Error(errorMessage) as Error & { code?: string; statusCode?: number };
      error.code = json.error?.code;
      error.statusCode = response.status;
      throw error;
    }

    return json.data;
  }
}
